import {
	DatePicker,
	FormFieldControl,
	Input,
	Select,
	SlideoutMenu,
	ToastCustom,
} from "@components";
import { Button } from "@components/button/button-untitledui";
import { OdometerTimePicker } from "@features/vehicle/components/odometer-time-picker";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	CalendarDate,
	type DateValue,
	getLocalTimeZone,
	today,
} from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import { Car01, Save01, Trash01 } from "@untitledui/icons";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import {
	INFRACTION_API_STATUS,
	INFRACTION_DISPLAY_STATUS,
} from "../constants/infraction-api-enums.constant";
import {
	INFRACTION_AIT_LENGTH,
	INFRACTION_API_SEVERITY_LABEL,
	INFRACTION_CREATE_ATTACHMENT_REQUIRED_MESSAGE,
	INFRACTION_NOTICE_ATTACHMENT_REQUIRED_ON_EDIT_MESSAGE,
} from "../constants/infraction-create.constant";
import {
	buildOccurredAtIso,
	formatCurrencyBR,
	formatNumberToDisplayBR,
	type InfractionCatalogSelectItem,
	type InfractionIssuingAuthoritySelectItem,
	type InfractionVehicleSelectItem,
	mapCatalogLookupToSelectItems,
	mapInfractionVehicleLookupToSelectItem,
	mapIssuingAuthorityToCodeSelectItems,
	mapIssuingAuthorityToNameSelectItems,
	mergeCatalogSelectItems,
	mergeIssuingAuthorityLookupItems,
	parseDisplayBRToNumber,
	resolveOptionalDateField,
} from "../handlers/infraction-create-form.helpers";
import {
	applyDetailToEditForm,
	buildUpdateInfractionPayload,
	hydrateEditLookupsFromDetail,
	resolveAttachmentSlotsSyncFromDetail,
} from "../handlers/infraction-edit-drawer.helpers";
import { applyInfractionApiFieldErrors } from "../handlers/map-infraction-api-field-errors";
import {
	type InfractionAttachmentSlotKey,
	type InfractionAttachmentSlots,
	isInfractionNoticeMissingAfterRemoval,
	resolveDisplayedAttachmentSlots,
} from "../handlers/map-infraction-attachments-to-slots";
import { useInfractionDetailRaw } from "../query/infraction-detail.query";
import { useCreateManualInfraction } from "../query/infractions-create.mutation";
import { useUpdateInfraction } from "../query/infractions-update.mutation";
import {
	type InfractionCreateSchema,
	infractionCreateSchema,
} from "../schemas/infraction-create.schema";
import {
	getDriverLookup,
	getInfractionCatalogLookup,
	getInfractionIssuingAuthoritiesLookup,
	getInfractionVehiclesLookup,
} from "../services/infractions.service";
import type { InfractionIssuingAuthorityLookupApiResponse } from "../types/infraction-api.types";
import { sanitizeInfractionAit } from "../utils/infraction-ait";
import {
	buildInfractionAttachmentFiles,
	hasInfractionAttachmentFiles,
	InfractionCreateAttachmentsSection,
} from "./infraction-create-attachments-section";
import { InfractionCreateDriverSection } from "./infraction-create-driver-section";
import { InfractionEditStatusSection } from "./infraction-edit-status-section";

type InfractionCreateDrawerMode = "create" | "edit";

type InfractionCreateDrawerProps = {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	mode?: InfractionCreateDrawerMode;
	infractionId?: string;
	onCreated?: () => void;
	onSaved?: () => void;
};

const defaultValues: InfractionCreateSchema = {
	vehicleId: 0,
	ait: "",
	issuingAuthorityId: 0,
	issuingAuthorityCode: "",
	isAnttInfraction: false,
	occurredDate: "",
	occurredTime: "",
	location: "",
	infractionCode: "",
	breakdown: "",
	description: "",
	severity: null,
	points: null,
	offenderType: 0,
	originalAmount: 0,
	driverIndicationDeadline: "",
	dueDate: "",
	paymentDate: "",
	driverId: null,
	transferPointsToDriver: false,
	infractionNoticeFile: null,
	penaltyNotificationFile: null,
	receiptsFile: null,
};

function stringToDateValue(str: string): CalendarDate | null {
	if (!str) {
		return null;
	}
	const [year, month, day] = str.split("-").map(Number);
	if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
		return null;
	}
	return new CalendarDate(year, month, day);
}

function dateValueToString(date: DateValue | null): string {
	if (!date) {
		return "";
	}
	const year = date.year;
	const month = String(date.month).padStart(2, "0");
	const day = String(date.day).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function SectionTitle({ children }: { children: string }) {
	return (
		<h3 className="font-bold text-fg-primary text-md leading-6">{children}</h3>
	);
}

function AnttStatusBadge({ isActive }: { isActive: boolean }) {
	if (!isActive) {
		return (
			<span className="inline-flex items-center gap-1 rounded-full border border-border-secondary bg-bg-secondary px-2 py-1 font-medium text-fg-secondary text-xs">
				Não
			</span>
		);
	}

	return (
		<span className="inline-flex items-center gap-1 rounded-full border border-utility-success-200 bg-utility-success-50 px-2 py-1 font-medium text-utility-success-700 text-xs">
			<span className="size-1.5 rounded-full bg-utility-success-500" />
			Sim
		</span>
	);
}

export function InfractionCreateDrawer({
	isOpen,
	onOpenChange,
	mode = "create",
	infractionId,
	onCreated,
	onSaved,
}: InfractionCreateDrawerProps) {
	const isEditMode = mode === "edit";
	const [authoritySearch, setAuthoritySearch] = useState("");
	const [authorityCodeInputValue, setAuthorityCodeInputValue] = useState("");
	const [authorityNameInputValue, setAuthorityNameInputValue] = useState("");
	const [selectedAuthorityLookup, setSelectedAuthorityLookup] =
		useState<InfractionIssuingAuthorityLookupApiResponse | null>(null);
	const skipAuthoritySearchRef = useRef(false);
	const [catalogSearch, setCatalogSearch] = useState("");
	const [catalogInputValue, setCatalogInputValue] = useState("");
	const [selectedCatalogItem, setSelectedCatalogItem] =
		useState<InfractionCatalogSelectItem | null>(null);
	const skipCatalogSearchRef = useRef(false);
	const editFormHydratedRef = useRef(false);
	const hydratedDetailUpdatedAtRef = useRef(0);
	const [areLookupsInitialized, setAreLookupsInitialized] = useState(false);
	const [driverSearch, setDriverSearch] = useState("");
	const [amountDisplay, setAmountDisplay] = useState("");
	const [statusOverride, setStatusOverride] = useState<string | null>(null);
	const maxOccurredDate = today(getLocalTimeZone());
	const [statusFieldError, setStatusFieldError] = useState<string | null>(null);
	const [editVehicleDisplay, setEditVehicleDisplay] =
		useState<InfractionVehicleSelectItem | null>(null);
	const [originalAttachmentSlots, setOriginalAttachmentSlots] =
		useState<InfractionAttachmentSlots>({
			infractionNotice: null,
			penaltyNotification: null,
			receipts: null,
		});
	const [removedAttachmentSlotKeys, setRemovedAttachmentSlotKeys] = useState<
		Set<InfractionAttachmentSlotKey>
	>(() => new Set());

	const displayedAttachmentSlots = resolveDisplayedAttachmentSlots(
		originalAttachmentSlots,
		removedAttachmentSlotKeys
	);

	const debouncedSetAuthoritySearch = useMemo(
		() =>
			debounce((value: string) => {
				setAuthoritySearch(value);
			}, 1000),
		[]
	);

	const debouncedSetCatalogSearch = useMemo(
		() =>
			debounce((value: string) => {
				setCatalogSearch(value);
			}, 1000),
		[]
	);

	useEffect(
		() => () => {
			debouncedSetAuthoritySearch.cancel();
			debouncedSetCatalogSearch.cancel();
		},
		[debouncedSetAuthoritySearch, debouncedSetCatalogSearch]
	);

	const debouncedSetDriverSearch = useCallback(
		debounce((value: string) => {
			setDriverSearch(value);
		}, 1000),
		[]
	);

	const { mutate: createInfraction, isPending: isCreating } =
		useCreateManualInfraction();
	const { mutate: updateInfraction, isPending: isUpdating } =
		useUpdateInfraction();
	const isSubmitting = isCreating || isUpdating;

	const {
		data: detailRaw,
		isLoading: isDetailLoading,
		dataUpdatedAt: detailUpdatedAt,
	} = useInfractionDetailRaw(infractionId ?? null, isOpen && isEditMode);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		getValues,
		setError,
		clearErrors,
		watch,
		formState: { errors },
	} = useForm<InfractionCreateSchema>({
		resolver: zodResolver(infractionCreateSchema),
		defaultValues,
		mode: "onSubmit",
	});

	const selectedVehicleId = watch("vehicleId");
	const selectedDriverId = watch("driverId");
	const isAnttInfraction = watch("isAnttInfraction");
	const severity = watch("severity");
	const points = watch("points");
	const transferPointsToDriver = watch("transferPointsToDriver");

	const { data: vehiclesData = [] } = useQuery({
		queryKey: ["infractions", "vehicles-lookup"],
		queryFn: () => getInfractionVehiclesLookup(),
		enabled: isOpen,
		staleTime: 1000 * 60 * 15,
	});

	const { data: issuingAuthoritiesData = [] } = useQuery({
		queryKey: ["infractions", "issuing-authorities", authoritySearch],
		queryFn: () => getInfractionIssuingAuthoritiesLookup(authoritySearch),
		enabled: isOpen && areLookupsInitialized,
	});

	const { data: catalogData = [] } = useQuery({
		queryKey: ["infractions", "catalog-lookup", catalogSearch],
		queryFn: () => getInfractionCatalogLookup(catalogSearch),
		enabled: isOpen && areLookupsInitialized,
	});

	const { data: drivers = [] } = useQuery({
		queryKey: ["infractions", "drivers-lookup", driverSearch],
		queryFn: () => getDriverLookup(driverSearch),
		enabled: isOpen,
	});

	const issuingAuthoritiesMerged = useMemo(
		() =>
			mergeIssuingAuthorityLookupItems(
				issuingAuthoritiesData,
				selectedAuthorityLookup
			),
		[issuingAuthoritiesData, selectedAuthorityLookup]
	);

	const issuingAuthorityCodeItems = useMemo(
		() => mapIssuingAuthorityToCodeSelectItems(issuingAuthoritiesMerged),
		[issuingAuthoritiesMerged]
	);

	const issuingAuthorityNameItems = useMemo(
		() => mapIssuingAuthorityToNameSelectItems(issuingAuthoritiesMerged),
		[issuingAuthoritiesMerged]
	);

	const catalogItems = useMemo(
		() =>
			mergeCatalogSelectItems(
				mapCatalogLookupToSelectItems(catalogData),
				selectedCatalogItem
			),
		[catalogData, selectedCatalogItem]
	);

	const vehicleItems = useMemo(
		() => vehiclesData.map(mapInfractionVehicleLookupToSelectItem),
		[vehiclesData]
	);

	const selectedVehicle =
		vehicleItems.find((vehicle) => vehicle.value === selectedVehicleId) ??
		(isEditMode ? editVehicleDisplay : null);

	const selectedDriver =
		drivers.find((driver) => driver.id === selectedDriverId) ??
		(isEditMode &&
		selectedDriverId !== null &&
		detailRaw?.driverId === selectedDriverId &&
		detailRaw?.driverName
			? {
					id: detailRaw.driverId,
					name: detailRaw.driverName,
					cpf: null,
				}
			: undefined);

	useEffect(() => {
		if (isOpen) {
			return;
		}

		editFormHydratedRef.current = false;
		hydratedDetailUpdatedAtRef.current = 0;
		setAreLookupsInitialized(false);
		debouncedSetAuthoritySearch.cancel();
		debouncedSetCatalogSearch.cancel();
		reset(defaultValues);
		setAuthoritySearch("");
		setAuthorityCodeInputValue("");
		setAuthorityNameInputValue("");
		setSelectedAuthorityLookup(null);
		skipAuthoritySearchRef.current = false;
		setCatalogSearch("");
		setCatalogInputValue("");
		setSelectedCatalogItem(null);
		skipCatalogSearchRef.current = false;
		setDriverSearch("");
		setAmountDisplay("");
		setStatusOverride(null);
		setStatusFieldError(null);
		setEditVehicleDisplay(null);
		setOriginalAttachmentSlots({
			infractionNotice: null,
			penaltyNotification: null,
			receipts: null,
		});
		setRemovedAttachmentSlotKeys(new Set());
	}, [isOpen, reset, debouncedSetAuthoritySearch, debouncedSetCatalogSearch]);

	useEffect(() => {
		if (!(isOpen && isEditMode)) {
			if (isOpen) {
				setAreLookupsInitialized(true);
			}
			return;
		}

		if (!detailRaw || editFormHydratedRef.current) {
			return;
		}

		editFormHydratedRef.current = true;
		hydratedDetailUpdatedAtRef.current = detailUpdatedAt;

		applyDetailToEditForm({
			detail: detailRaw,
			reset,
			setAmountDisplay,
			setStatusOverride,
			setEditVehicleDisplay,
		});

		const lookups = hydrateEditLookupsFromDetail(detailRaw);
		if (lookups.catalogItem) {
			setSelectedCatalogItem(lookups.catalogItem);
			setCatalogInputValue(lookups.catalogItem.label);
		}
		setCatalogSearch(lookups.catalogSearch);

		if (lookups.authorityLookup) {
			setSelectedAuthorityLookup(lookups.authorityLookup);
			setAuthorityCodeInputValue(lookups.authorityLookup.code ?? "");
			setAuthorityNameInputValue(
				lookups.authorityLookup.description ??
					lookups.authorityLookup.code ??
					""
			);
		}
		setAuthoritySearch(lookups.authoritySearch);
		setOriginalAttachmentSlots(lookups.attachmentSlots);
		setRemovedAttachmentSlotKeys(new Set());
		setAreLookupsInitialized(true);
	}, [isOpen, isEditMode, detailRaw, detailUpdatedAt, reset]);

	useEffect(() => {
		if (!(isOpen && isEditMode && detailRaw && editFormHydratedRef.current)) {
			return;
		}

		if (detailUpdatedAt <= hydratedDetailUpdatedAtRef.current) {
			return;
		}

		const syncResult = resolveAttachmentSlotsSyncFromDetail(
			detailRaw,
			getValues(),
			removedAttachmentSlotKeys
		);

		if (syncResult.shouldSync) {
			setOriginalAttachmentSlots(syncResult.attachmentSlots);
			setRemovedAttachmentSlotKeys(new Set());
		}

		hydratedDetailUpdatedAtRef.current = detailUpdatedAt;
	}, [
		isOpen,
		isEditMode,
		detailRaw,
		detailUpdatedAt,
		getValues,
		removedAttachmentSlotKeys,
	]);

	const handleCatalogInputChange = (value: string) => {
		setCatalogInputValue(value);

		if (value.trim().length === 0) {
			skipCatalogSearchRef.current = false;
			debouncedSetCatalogSearch.cancel();
			setCatalogSearch("");
			setSelectedCatalogItem(null);
			setValue("infractionCode", "");
			setValue("breakdown", "");
			setValue("description", "");
			setValue("severity", null);
			setValue("points", null);
			setValue("offenderType", 0);
			return;
		}

		if (skipCatalogSearchRef.current) {
			skipCatalogSearchRef.current = false;
			return;
		}

		if (selectedCatalogItem && value !== selectedCatalogItem.label) {
			setSelectedCatalogItem(null);
		}

		debouncedSetCatalogSearch(value);
	};

	const applyCatalogSelection = (catalogItem: InfractionCatalogSelectItem) => {
		skipCatalogSearchRef.current = true;
		setSelectedCatalogItem(catalogItem);
		setCatalogInputValue(catalogItem.label);
		setValue("infractionCode", catalogItem.code ?? "");
		setValue("breakdown", catalogItem.breakdown ?? "");
		setValue("description", catalogItem.description ?? "");
		setValue("severity", catalogItem.severity ?? null, {
			shouldValidate: true,
		});
		setValue("points", catalogItem.points ?? null);
		setValue("offenderType", catalogItem.offenderType ?? 0, {
			shouldValidate: true,
		});

		if (
			catalogItem.baseAmount !== null &&
			catalogItem.baseAmount !== undefined
		) {
			setValue("originalAmount", catalogItem.baseAmount);
			setAmountDisplay(formatNumberToDisplayBR(catalogItem.baseAmount));
		}
	};

	const editDetail =
		isEditMode && detailRaw !== undefined && detailRaw !== null
			? detailRaw
			: null;

	const isPaidInfraction =
		editDetail?.status === INFRACTION_API_STATUS.PAID ||
		editDetail?.displayStatus === INFRACTION_DISPLAY_STATUS.PAID;

	const isEditLoading = isEditMode && isDetailLoading;

	const close = () => {
		onOpenChange(false);
	};

	const applyAuthoritySelection = (
		authority: InfractionIssuingAuthoritySelectItem
	) => {
		skipAuthoritySearchRef.current = true;
		setSelectedAuthorityLookup({
			id: authority.id,
			code: authority.code ?? authority.codeLabel,
			description: authority.description ?? authority.label,
			isAntt: authority.isAntt,
		});
		setAuthorityCodeInputValue(authority.codeLabel);
		setAuthorityNameInputValue(
			authority.description ?? authority.label ?? authority.codeLabel
		);
		setValue("issuingAuthorityId", authority.id, { shouldValidate: true });
		setValue("issuingAuthorityCode", authority.codeLabel);
		setValue("isAnttInfraction", authority.isAntt);
	};

	const handleAuthorityCodeInputChange = (value: string) => {
		setAuthorityCodeInputValue(value);

		if (value.trim().length === 0) {
			skipAuthoritySearchRef.current = false;
			debouncedSetAuthoritySearch.cancel();
			setAuthoritySearch("");
			setSelectedAuthorityLookup(null);
			setAuthorityNameInputValue("");
			setValue("issuingAuthorityId", 0);
			return;
		}

		if (skipAuthoritySearchRef.current) {
			skipAuthoritySearchRef.current = false;
			return;
		}

		if (
			selectedAuthorityLookup &&
			value !== (selectedAuthorityLookup.code ?? "")
		) {
			setSelectedAuthorityLookup(null);
			setValue("issuingAuthorityId", 0);
		}

		debouncedSetAuthoritySearch(value);
	};

	const handleAuthorityNameInputChange = (value: string) => {
		setAuthorityNameInputValue(value);

		if (value.trim().length === 0) {
			skipAuthoritySearchRef.current = false;
			debouncedSetAuthoritySearch.cancel();
			setAuthoritySearch("");
			setSelectedAuthorityLookup(null);
			setAuthorityCodeInputValue("");
			setValue("issuingAuthorityId", 0);
			return;
		}

		if (skipAuthoritySearchRef.current) {
			skipAuthoritySearchRef.current = false;
			return;
		}

		const selectedName =
			selectedAuthorityLookup?.description ??
			selectedAuthorityLookup?.code ??
			"";

		if (selectedAuthorityLookup && value !== selectedName) {
			setSelectedAuthorityLookup(null);
			setValue("issuingAuthorityId", 0);
		}

		debouncedSetAuthoritySearch(value);
	};

	const submitEditInfraction = (values: InfractionCreateSchema) => {
		if (!(infractionId && detailRaw)) {
			return;
		}

		clearErrors();
		setStatusFieldError(null);

		if (
			isInfractionNoticeMissingAfterRemoval(
				values,
				originalAttachmentSlots,
				removedAttachmentSlotKeys
			)
		) {
			setError("infractionNoticeFile", {
				type: "manual",
				message: INFRACTION_NOTICE_ATTACHMENT_REQUIRED_ON_EDIT_MESSAGE,
			});
			return;
		}

		const resolvedStatus = statusOverride ? Number(statusOverride) : undefined;
		const payload = buildUpdateInfractionPayload(
			values,
			detailRaw,
			resolvedStatus
		);

		updateInfraction(
			{
				infractionId: Number(infractionId),
				vehicleId: detailRaw.vehicleId,
				payload,
				attachmentValues: {
					infractionNoticeFile: values.infractionNoticeFile,
					penaltyNotificationFile: values.penaltyNotificationFile,
					receiptsFile: values.receiptsFile,
				},
				originalAttachmentSlots,
				removedAttachmentSlotKeys: Array.from(removedAttachmentSlotKeys),
			},
			{
				onSuccess: () => {
					close();
					onSaved?.();
				},
				onError: (error) => {
					const hasFieldErrors = applyInfractionApiFieldErrors(
						error,
						setError,
						setStatusFieldError
					);

					if (hasFieldErrors) {
						return;
					}

					const message =
						error instanceof Error
							? error.message
							: "Não foi possível atualizar a infração.";
					toast(
						ToastCustom({
							description: message,
							title: "Erro ao atualizar",
							variant: "error",
						})
					);
				},
			}
		);
	};

	const submitCreateInfraction = (values: InfractionCreateSchema) => {
		clearErrors();

		if (!hasInfractionAttachmentFiles(values)) {
			setError("infractionNoticeFile", {
				type: "manual",
				message: INFRACTION_CREATE_ATTACHMENT_REQUIRED_MESSAGE,
			});
			return;
		}

		const attachmentFiles = buildInfractionAttachmentFiles(values);
		const dueDate = resolveOptionalDateField(values.dueDate);

		createInfraction(
			{
				payload: {
					vehicleId: values.vehicleId,
					driverId: values.driverId ?? null,
					transferPointsToDriver: values.transferPointsToDriver,
					issuingAuthorityId: values.issuingAuthorityId,
					ait: values.ait.trim().toUpperCase(),
					infractionCode: values.infractionCode || null,
					breakdown: values.breakdown || null,
					description: values.description || null,
					occurredAt: buildOccurredAtIso(
						values.occurredDate,
						values.occurredTime
					),
					location: values.location || null,
					severity:
						values.severity !== null &&
						values.severity !== undefined &&
						values.severity > 0
							? values.severity
							: null,
					points: values.points ?? null,
					originalAmount: values.originalAmount,
					offenderType: values.offenderType,
					driverIndicationDeadline: values.driverIndicationDeadline || null,
					dueDate,
					paymentDate: values.paymentDate || null,
				},
				attachmentFiles,
			},
			{
				onSuccess: () => {
					close();
					onCreated?.();
				},
				onError: (error) => {
					const hasFieldErrors = applyInfractionApiFieldErrors(
						error,
						setError,
						setStatusFieldError
					);

					if (hasFieldErrors) {
						return;
					}

					const message =
						error instanceof Error
							? error.message
							: "Não foi possível cadastrar a infração.";
					toast(
						ToastCustom({
							description: message,
							title: "Erro ao cadastrar",
							variant: "error",
						})
					);
				},
			}
		);
	};

	const onSubmit = handleSubmit((values) => {
		if (isEditMode) {
			submitEditInfraction(values);
			return;
		}

		submitCreateInfraction(values);
	});

	return (
		<SlideoutMenu
			className="w-full max-w-[625px]"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			style={{ zIndex: 10 }}
		>
			{/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: form sections mirror Figma layout */}
			{({ close: closeDrawer }) => (
				<form
					className="flex h-full min-h-0 w-full flex-col"
					onSubmit={onSubmit}
				>
					<SlideoutMenu.Header
						className="py-4 md:px-4"
						onClose={() => {
							closeDrawer();
							close();
						}}
					>
						<div className="flex flex-col gap-1.5 pr-8">
							<h2 className="font-bold text-fg-primary text-xl">
								{isEditMode ? "Editar Infração" : "Adicionar Nova Infração"}
							</h2>
							<p className="font-medium text-fg-secondary text-sm">
								{isEditMode
									? "Atualize os dados da infração cadastrada manualmente"
									: "Insira os dados da autuação para iniciar o monitoramento de prazos"}
							</p>
						</div>
					</SlideoutMenu.Header>

					<SlideoutMenu.Content className="md:px-4">
						{isEditLoading ? (
							<div className="py-12 text-center text-fg-secondary text-sm">
								Carregando dados da infração...
							</div>
						) : (
							<div className="flex flex-col gap-8 pb-6">
								{editDetail ? (
									<InfractionEditStatusSection
										displayStatus={editDetail.displayStatus}
										error={statusFieldError ?? undefined}
										onStatusOverrideChange={(value) => {
											setStatusOverride(value);
											setStatusFieldError(null);
										}}
										statusCode={editDetail.status}
										statusLabel={
											editDetail.displayStatusLabel ??
											editDetail.statusLabel ??
											""
										}
										statusOverride={statusOverride}
									/>
								) : null}

								{isEditMode ? null : (
									<section className="flex flex-col gap-4">
										<SectionTitle>Veículo</SectionTitle>

										<FormFieldControl
											error={errors.vehicleId?.message}
											htmlFor="infraction-vehicle-search"
											isRequired
											label="Pesquisar por placa..."
										>
											<Select.ComboBox
												className="w-full"
												isDisabled={isSubmitting || isDetailLoading}
												isInvalid={!!errors.vehicleId?.message}
												items={vehicleItems}
												placeholder="Digite a placa (ex: ABC-1234)"
												selectedKey={
													selectedVehicleId > 0
														? String(selectedVehicleId)
														: null
												}
												shortcut={false}
											>
												{(item) => (
													<Select.Item
														id={item.id}
														isDisabled={item.isDisabled}
														onClick={() => {
															clearErrors("vehicleId");
															setValue("vehicleId", Number(item.id), {
																shouldValidate: true,
															});
														}}
														textValue={item.label}
													>
														{item.label}
													</Select.Item>
												)}
											</Select.ComboBox>
										</FormFieldControl>

										{selectedVehicle ? (
											<div className="flex items-center justify-between rounded-lg border border-border-primary bg-bg-primary px-5 py-2.5 shadow-xs">
												<div className="flex items-center gap-4">
													<div className="flex size-12 items-center justify-center rounded-[10px] bg-bg-brand-secondary">
														<Car01 className="size-5 text-fg-brand-primary" />
													</div>
													<p className="font-bold text-fg-primary text-md">
														{selectedVehicle.displayTitle}
													</p>
												</div>
												<Button
													aria-label="Remover veículo selecionado"
													className="p-1"
													color="link-gray"
													onClick={() => {
														setValue("vehicleId", 0);
													}}
													size="sm"
													tabIndex={0}
													type="button"
												>
													<Trash01 className="size-[18px] text-fg-quaternary" />
												</Button>
											</div>
										) : null}
									</section>
								)}

								<section className="flex flex-col gap-4">
									<SectionTitle>Informações da Multa</SectionTitle>

									<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
										<Controller
											control={control}
											name="ait"
											render={({ field }) => (
												<FormFieldControl
													error={errors.ait?.message}
													htmlFor="infraction-ait"
													isRequired
													label="Código do Auto"
													size="sm"
												>
													<Input
														{...field}
														error={!!errors.ait?.message}
														id="infraction-ait"
														maxLength={
															isAnttInfraction
																? INFRACTION_AIT_LENGTH.ANTT
																: INFRACTION_AIT_LENGTH.STANDARD
														}
														onChange={(event) => {
															field.onChange(
																sanitizeInfractionAit(
																	event.target.value,
																	isAnttInfraction
																)
															);
														}}
														placeholder={
															isAnttInfraction
																? "16 caracteres"
																: "10 caracteres"
														}
													/>
												</FormFieldControl>
											)}
										/>

										<FormFieldControl
											error={errors.issuingAuthorityId?.message}
											htmlFor="infraction-authority-code"
											isRequired
											label="Código do Órgão Emissor"
											size="sm"
										>
											<Select.ComboBox
												allowsEmptyCollection={
													issuingAuthorityCodeItems.length > 0
												}
												inputValue={authorityCodeInputValue}
												isDisabled={isSubmitting || isDetailLoading}
												isInvalid={!!errors.issuingAuthorityId?.message}
												items={issuingAuthorityCodeItems}
												menuTrigger="focus"
												onInputChange={handleAuthorityCodeInputChange}
												onSelectionChange={(key) => {
													if (key === null || key === undefined) {
														return;
													}

													const authority = issuingAuthorityCodeItems.find(
														(item) => String(item.id) === String(key)
													);

													if (authority) {
														clearErrors("issuingAuthorityId");
														applyAuthoritySelection(authority);
													}
												}}
												placeholder="Ex: 100"
												selectedKey={selectedAuthorityLookup?.id ?? null}
												shortcut={false}
											>
												{(item) => (
													<Select.Item
														id={item.id}
														isDisabled={item.isDisabled}
														textValue={item.label}
													>
														{item.label}
													</Select.Item>
												)}
											</Select.ComboBox>
										</FormFieldControl>

										<FormFieldControl
											error={errors.issuingAuthorityId?.message}
											htmlFor="infraction-authority"
											isRequired
											label="Órgão Emissor"
											size="sm"
										>
											<Select.ComboBox
												allowsEmptyCollection={
													issuingAuthorityNameItems.length > 0
												}
												inputValue={authorityNameInputValue}
												isDisabled={isSubmitting || isDetailLoading}
												isInvalid={!!errors.issuingAuthorityId?.message}
												items={issuingAuthorityNameItems}
												menuTrigger="focus"
												onInputChange={handleAuthorityNameInputChange}
												onSelectionChange={(key) => {
													if (key === null || key === undefined) {
														return;
													}

													const authority = issuingAuthorityNameItems.find(
														(item) => String(item.id) === String(key)
													);

													if (authority) {
														clearErrors("issuingAuthorityId");
														applyAuthoritySelection(authority);
													}
												}}
												placeholder="Ex: Órgão Emissor"
												placement="bottom right"
												popoverClassName="max-w-full !w-auto"
												selectedKey={selectedAuthorityLookup?.id ?? null}
												shortcut={false}
											>
												{(item) => (
													<Select.Item
														id={item.id}
														isDisabled={item.isDisabled}
														textValue={item.label}
													>
														{item.label}
													</Select.Item>
												)}
											</Select.ComboBox>
										</FormFieldControl>
									</div>

									<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
										<Controller
											control={control}
											name="occurredDate"
											render={({ field }) => (
												<FormFieldControl
													error={errors.occurredDate?.message}
													htmlFor="infraction-occurred-date"
													isRequired
													label="Data"
													size="sm"
												>
													<DatePicker
														aria-label="Data da infração"
														className="w-full"
														id="infraction-occurred-date"
														isDisabled={isSubmitting || isDetailLoading}
														isInvalid={!!errors.occurredDate?.message}
														maxValue={maxOccurredDate}
														onChange={(date) => {
															field.onChange(dateValueToString(date));
														}}
														value={stringToDateValue(field.value ?? "")}
													/>
												</FormFieldControl>
											)}
										/>

										<Controller
											control={control}
											name="occurredTime"
											render={({ field }) => (
												<FormFieldControl
													error={errors.occurredTime?.message}
													htmlFor="infraction-occurred-time"
													isRequired
													label="Hora"
													size="sm"
												>
													<OdometerTimePicker
														className="rounded-lg"
														isInvalid={!!errors.occurredTime?.message}
														onChange={field.onChange}
														placeholder="Escolha a hora"
														value={field.value}
													/>
												</FormFieldControl>
											)}
										/>

										<Controller
											control={control}
											name="location"
											render={({ field }) => (
												<FormFieldControl
													error={errors.location?.message}
													htmlFor="infraction-location"
													isRequired
													label="Local"
													size="sm"
												>
													<Input
														{...field}
														error={!!errors.location?.message}
														id="infraction-location"
														placeholder="Rua, Avenida..."
													/>
												</FormFieldControl>
											)}
										/>
									</div>

									<div className="flex items-center justify-between rounded-lg border border-border-primary bg-bg-primary/50 p-4">
										<div>
											<p className="font-bold text-fg-primary text-sm">
												Infração ANTT
											</p>
											<p className="text-fg-secondary text-xs">
												Infração relacionada ao transporte terrestre
											</p>
										</div>
										<AnttStatusBadge isActive={isAnttInfraction} />
									</div>
								</section>

								{isEditMode ? (
									<section className="flex flex-col gap-4">
										<SectionTitle>Veículo</SectionTitle>
										<div className="pointer-events-none opacity-30">
											<FormFieldControl
												htmlFor="infraction-vehicle-search-edit"
												label="Pesquisar por placa..."
											>
												<Select.ComboBox
													allowsEmptyCollection
													className="w-full"
													isDisabled
													items={vehicleItems}
													placeholder="Digite a placa (ex: ABC-1234)"
													shortcut={false}
												>
													{(item) => (
														<Select.Item
															id={item.id}
															isDisabled
															textValue={item.label}
														>
															{item.label}
														</Select.Item>
													)}
												</Select.ComboBox>
											</FormFieldControl>
										</div>
										{selectedVehicle ? (
											<div className="flex items-center justify-between rounded-lg border border-border-primary bg-bg-primary px-5 py-2.5 shadow-xs">
												<div className="flex items-center gap-4">
													<div className="flex size-12 items-center justify-center rounded-[10px] bg-bg-brand-secondary">
														<Car01 className="size-5 text-fg-brand-primary" />
													</div>
													<p className="font-bold text-fg-primary text-md">
														{selectedVehicle.displayTitle}
													</p>
												</div>
											</div>
										) : null}
									</section>
								) : null}

								<InfractionCreateDriverSection
									drivers={drivers}
									isPending={isSubmitting || isDetailLoading}
									onDriverSearchChange={debouncedSetDriverSearch}
									onDriverSelect={(driverId) => {
										setValue("driverId", driverId);
									}}
									onRemoveDriver={() => {
										setValue("driverId", null);
										setValue("transferPointsToDriver", false);
									}}
									onTransferPointsChange={(value) => {
										setValue("transferPointsToDriver", value);
									}}
									selectedDriver={selectedDriver}
									transferPointsToDriver={transferPointsToDriver}
								/>

								<section className="flex flex-col gap-4">
									<SectionTitle>Tipificação</SectionTitle>

									<FormFieldControl
										error={
											errors.severity?.message || errors.offenderType?.message
										}
										htmlFor="infraction-catalog-search"
										isRequired
										label="Código ou Descrição da Infração"
										size="sm"
									>
										<Select.ComboBox
											allowsEmptyCollection={catalogItems.length > 0}
											className="w-full"
											inputValue={catalogInputValue}
											isDisabled={isSubmitting || isDetailLoading}
											isInvalid={
												!!(
													errors.severity?.message ||
													errors.offenderType?.message
												)
											}
											items={catalogItems}
											menuTrigger="focus"
											onInputChange={handleCatalogInputChange}
											onSelectionChange={(key) => {
												if (key === null || key === undefined) {
													return;
												}

												const catalogItem = catalogItems.find(
													(item) => String(item.id) === String(key)
												);

												if (catalogItem) {
													clearErrors(["severity", "offenderType"]);
													applyCatalogSelection(catalogItem);
												}
											}}
											placeholder="Ex: 7455-0 ou Velocidade Superior...."
											selectedKey={selectedCatalogItem?.id ?? null}
											shortcut={false}
										>
											{(item) => (
												<Select.Item
													id={item?.id ?? ""}
													isDisabled={item?.isDisabled ?? false}
													textValue={item?.label ?? ""}
												>
													<div className="flex flex-1 gap-2">
														<span className="truncate">
															{item?.label ?? ""}
														</span>
														<span className="text-text-tertiary">
															{item?.supportingText ?? ""}
														</span>
													</div>
												</Select.Item>
											)}
										</Select.ComboBox>
									</FormFieldControl>

									<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
										<FormFieldControl
											htmlFor="infraction-severity"
											label="Tipo"
											size="sm"
										>
											<Input
												disabled
												placeholder="Grave"
												value={
													severity
														? (INFRACTION_API_SEVERITY_LABEL[severity] ?? "")
														: ""
												}
											/>
										</FormFieldControl>

										<FormFieldControl
											htmlFor="infraction-points"
											label="Pontuação"
											size="sm"
										>
											<Input
												disabled
												placeholder="5 pontos"
												value={
													points !== null && points !== undefined
														? `${points} pontos`
														: ""
												}
											/>
										</FormFieldControl>
									</div>

									<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
										<Controller
											control={control}
											name="driverIndicationDeadline"
											render={({ field }) => (
												<FormFieldControl
													error={errors.driverIndicationDeadline?.message}
													htmlFor="infraction-indication-deadline"
													label="Data Limite para Indicação"
													size="sm"
												>
													<DatePicker
														aria-label="Data limite para indicação"
														className="w-full"
														id="infraction-indication-deadline"
														isDisabled={isSubmitting || isDetailLoading}
														isInvalid={
															!!errors.driverIndicationDeadline?.message
														}
														onChange={(date) => {
															clearErrors("driverIndicationDeadline");
															field.onChange(dateValueToString(date));
														}}
														value={stringToDateValue(field.value ?? "")}
													/>
												</FormFieldControl>
											)}
										/>

										<Controller
											control={control}
											name="originalAmount"
											render={({ field }) => (
												<FormFieldControl
													error={errors.originalAmount?.message}
													htmlFor="infraction-amount"
													isRequired
													label="Valor da Infração (R$)"
													size="sm"
												>
													<Input
														error={!!errors.originalAmount?.message}
														id="infraction-amount"
														inputMode="decimal"
														maxLength={10}
														onChange={(event) => {
															const formatted = formatCurrencyBR(
																event.target.value
															);
															setAmountDisplay(formatted);
															const parsed = parseDisplayBRToNumber(formatted);
															field.onChange(parsed ?? 0);
														}}
														placeholder="R$"
														value={amountDisplay}
													/>
												</FormFieldControl>
											)}
										/>
									</div>

									<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
										<Controller
											control={control}
											name="dueDate"
											render={({ field }) => (
												<FormFieldControl
													error={errors.dueDate?.message}
													htmlFor="infraction-due-date"
													label="Data de Vencimento"
													size="sm"
												>
													<DatePicker
														aria-label="Data de vencimento"
														className="w-full"
														id="infraction-due-date"
														isDisabled={isSubmitting || isDetailLoading}
														isInvalid={!!errors.dueDate?.message}
														onChange={(date) => {
															clearErrors("dueDate");
															field.onChange(dateValueToString(date));
														}}
														value={stringToDateValue(field.value ?? "")}
													/>
												</FormFieldControl>
											)}
										/>

										<Controller
											control={control}
											name="paymentDate"
											render={({ field }) => (
												<FormFieldControl
													error={errors.paymentDate?.message}
													htmlFor="infraction-payment-date"
													label="Data de Pagamento"
													size="sm"
												>
													<DatePicker
														aria-label="Data de pagamento"
														className="w-full"
														id="infraction-payment-date"
														isDisabled={
															isSubmitting ||
															isDetailLoading ||
															isPaidInfraction
														}
														isInvalid={!!errors.paymentDate?.message}
														onChange={(date) => {
															clearErrors("paymentDate");
															field.onChange(dateValueToString(date));
														}}
														value={stringToDateValue(field.value ?? "")}
													/>
												</FormFieldControl>
											)}
										/>
									</div>
								</section>

								<InfractionCreateAttachmentsSection
									allowRemoveExisting={isEditMode}
									control={control}
									errors={{
										infractionNoticeFile: errors.infractionNoticeFile?.message,
										penaltyNotificationFile:
											errors.penaltyNotificationFile?.message,
										receiptsFile: errors.receiptsFile?.message,
									}}
									existingAttachments={
										isEditMode ? displayedAttachmentSlots : null
									}
									isPending={isSubmitting || isDetailLoading}
									onAttachmentChange={() => {
										clearErrors([
											"infractionNoticeFile",
											"penaltyNotificationFile",
											"receiptsFile",
										]);
									}}
									onRemoveExistingAttachment={(slot) => {
										clearErrors([
											"infractionNoticeFile",
											"penaltyNotificationFile",
											"receiptsFile",
										]);
										setRemovedAttachmentSlotKeys((previous) => {
											const next = new Set(previous);
											next.add(slot);
											return next;
										});
									}}
									requireAttachments={!isEditMode}
								/>
							</div>
						)}
					</SlideoutMenu.Content>

					<SlideoutMenu.Footer className="flex gap-3">
						<Button
							className="flex-1"
							color="secondary"
							isDisabled={isSubmitting || isDetailLoading}
							onClick={() => {
								closeDrawer();
								close();
							}}
							size="md"
							tabIndex={0}
							type="button"
						>
							Cancelar
						</Button>
						<Button
							className="flex-1"
							color="primary"
							iconLeading={Save01}
							isLoading={isSubmitting || isDetailLoading}
							size="md"
							tabIndex={0}
							type="submit"
						>
							Salvar e Monitorar
						</Button>
					</SlideoutMenu.Footer>
				</form>
			)}
		</SlideoutMenu>
	);
}
